/**
 * Time Machine #114 Phase 6 — pure response parser for the Lambda's
 * `getBlackoutDatesAuditByKellerId` method.
 *
 * Same shape rules as parseEventLogResponse:
 *   - { ok: true,  kind: "success", records: [...], meta: {...}, lambdaVersion }
 *   - { ok: false, kind: "error",   reason: string, raw: ..., lambdaVersion? }
 *
 * Records are full BlackoutDatesAudit docs. The frontend renders each as a
 * "Customer Web Portal queried availability" row in the JobsDetailModal
 * History feed; click expands the full audit JSON, double-click scrubs the
 * time-travel slider to invokedAt.
 */

const EXPECTED_METHOD = "getBlackoutDatesAuditByKellerId";

export function parseBlackoutDatesAuditResponse(rawResponse) {
  if (rawResponse == null || typeof rawResponse !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response is not an object",
      raw: rawResponse,
    };
  }

  const { message, _debugVersion, errorType, errorMessage } = rawResponse;

  // Lambda runtime error
  if (errorType || errorMessage) {
    return {
      ok: false,
      kind: "error",
      reason: errorMessage || errorType || "Lambda error (no message)",
      lambdaErrorType: errorType || null,
      raw: rawResponse,
    };
  }

  // API Gateway error string
  if (typeof message === "string") {
    return {
      ok: false,
      kind: "error",
      reason: `API Gateway: ${message}`,
      raw: rawResponse,
    };
  }

  if (!message || typeof message !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response missing message envelope",
      raw: rawResponse,
    };
  }

  if (message.method !== EXPECTED_METHOD) {
    return {
      ok: false,
      kind: "error",
      reason: `Expected message.method "${EXPECTED_METHOD}" (got ${JSON.stringify(message.method)})`,
      raw: rawResponse,
    };
  }

  const result = message.result;
  if (!result || typeof result !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response missing message.result",
      raw: rawResponse,
    };
  }

  // Lambda-side validation error (e.g. missing kellerId, bad date)
  if (result.error) {
    return {
      ok: false,
      kind: "error",
      reason: result.error,
      raw: rawResponse,
      lambdaVersion: _debugVersion ?? null,
    };
  }

  if (!Array.isArray(result.records)) {
    return {
      ok: false,
      kind: "error",
      reason: "Response result.records is missing or not an array",
      raw: rawResponse,
    };
  }

  return {
    ok: true,
    kind: "success",
    records: result.records,
    meta: result.meta ?? null,
    lambdaVersion: _debugVersion ?? null,
  };
}
